import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0a0a2e] text-white py-24">
      <div className="max-w-4xl mx-auto px-6">
        <Button
          onClick={() => navigate('/')}
          variant="outline"
          className="mb-8 border-cyan-500/50 hover:border-cyan-400 bg-transparent text-white"
        >
          <ArrowLeft className="mr-2" size={20} />
          Back to Home
        </Button>

        <h1 className="text-4xl lg:text-5xl font-bold mb-4 bg-gradient-to-r from-cyan-400 to-pink-500 bg-clip-text text-transparent">
          Terms of Service
        </h1>
        <p className="text-gray-400 mb-8">Last updated: December 2025</p>

        <div className="space-y-8 text-gray-300">
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. Acceptance of Terms</h2>
            <p className="leading-relaxed">
              By accessing and using StreamShares ("the Platform"), you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. Description of Service</h2>
            <p className="leading-relaxed mb-4">
              StreamShares is a platform that enables:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Content creators to manage appearances in their content</li>
              <li>Fans to invest in creators and share in their revenue</li>
              <li>Transparent revenue sharing between creators and investors</li>
              <li>Protection against content appearance disputes</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. User Accounts</h2>
            <h3 className="text-xl font-semibold text-cyan-400 mb-3">3.1 Account Creation</h3>
            <p className="leading-relaxed mb-4">
              To use certain features of the Platform, you must create an account. You agree to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Be at least 18 years of age</li>
              <li>Notify us immediately of any unauthorized access</li>
            </ul>

            <h3 className="text-xl font-semibold text-cyan-400 mb-3 mt-6">3.2 Account Types</h3>
            <p className="leading-relaxed mb-4">
              We offer two account types:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Creator Accounts:</strong> For content creators seeking investment and managing appearances</li>
              <li><strong>Investor Accounts:</strong> For individuals investing in creators</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. Investment Terms</h2>
            <h3 className="text-xl font-semibold text-cyan-400 mb-3">4.1 Investment Process</h3>
            <p className="leading-relaxed mb-4">
              When investing in a creator:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Investments are subject to minimum and maximum limits</li>
              <li>Revenue sharing percentages are clearly disclosed before investment</li>
              <li>Investment terms range from 12 to 36 months</li>
              <li>All investments carry risk and returns are not guaranteed</li>
            </ul>

            <h3 className="text-xl font-semibold text-cyan-400 mb-3 mt-6">4.2 Revenue Sharing</h3>
            <p className="leading-relaxed mb-4">
              Revenue sharing operates as follows:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Payments are calculated and distributed monthly</li>
              <li>Creator revenue is verified through platform integrations</li>
              <li>Investors receive their share automatically via the platform</li>
              <li>StreamShares takes a platform fee from each transaction</li>
            </ul>

            <h3 className="text-xl font-semibold text-cyan-400 mb-3 mt-6">4.3 Investment Risks</h3>
            <p className="leading-relaxed mb-4">
              You acknowledge that:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>All investments carry financial risk</li>
              <li>Past performance does not guarantee future results</li>
              <li>Creator revenue may fluctuate or decline</li>
              <li>You may lose some or all of your investment</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. Creator Obligations</h2>
            <p className="leading-relaxed mb-4">
              As a creator, you agree to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Accurately report all revenue from connected platforms</li>
              <li>Maintain active content creation during investment periods</li>
              <li>Log all individuals appearing in your content</li>
              <li>Honor revenue sharing agreements with investors</li>
              <li>Not engage in fraudulent or deceptive practices</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. Prohibited Activities</h2>
            <p className="leading-relaxed mb-4">
              You may not:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Use the platform for illegal purposes</li>
              <li>Manipulate revenue data or investment information</li>
              <li>Harass, threaten, or abuse other users</li>
              <li>Attempt to gain unauthorized access to the platform</li>
              <li>Reverse engineer or copy platform features</li>
              <li>Create multiple accounts to circumvent restrictions</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. Payment Terms</h2>
            <p className="leading-relaxed mb-4">
              All payments are processed through secure third-party payment providers. You agree to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Pay all applicable fees and charges</li>
              <li>Provide valid payment information</li>
              <li>Accept responsibility for all transactions on your account</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. Intellectual Property</h2>
            <p className="leading-relaxed mb-4">
              The Platform and its content are owned by StreamShares and protected by intellectual property laws. You retain ownership of your content but grant us a license to use it for platform operations.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. Disclaimer of Warranties</h2>
            <p className="leading-relaxed">
              THE PLATFORM IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DO NOT GUARANTEE UNINTERRUPTED ACCESS, ERROR-FREE OPERATION, OR SPECIFIC RESULTS FROM USING THE PLATFORM.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10. Limitation of Liability</h2>
            <p className="leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, STREAMSHARES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFITS OR INVESTMENT LOSSES.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">11. Termination</h2>
            <p className="leading-relaxed mb-4">
              We may suspend or terminate your account if you violate these terms. Upon termination:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Existing investment agreements will be honored</li>
              <li>Pending revenue shares will be distributed</li>
              <li>Your access to the platform will be revoked</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">12. Governing Law</h2>
            <p className="leading-relaxed">
              These Terms are governed by the laws of England and Wales. Any disputes will be resolved in the courts of London, United Kingdom.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">13. Changes to Terms</h2>
            <p className="leading-relaxed">
              We may modify these Terms at any time. Continued use of the Platform after changes constitutes acceptance of the new terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">14. Contact Information</h2>
            <div className="mt-4 p-6 bg-gray-900/50 rounded-lg border border-gray-700/50">
              <p className="text-cyan-400 font-semibold mb-2">For questions about these Terms:</p>
              <p>Email: info@streamshares.co.uk</p>
              <p>Location: London, United Kingdom</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
